import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useCarouselSound } from '@/hooks/use-carousel-sound';
import { useKeepAwake } from '@/hooks/use-keep-awake';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Gift, LogOut, Volume2, VolumeX } from 'lucide-react';
import type { ReactNode } from 'react';

interface CarouselShellProps {
  children: ReactNode;
  /** `dark` is the customer-facing kiosk stage; `light` is the admin console. */
  variant?: 'light' | 'dark';
}

/** Standalone kiosk chrome — no CRM sidebar/nav. */
export function CarouselShell({ children, variant = 'light' }: CarouselShellProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { enabled: soundEnabled, setEnabled: setSoundEnabled } = useCarouselSound();
  const isDark = variant === 'dark';

  // The kiosk display must stay lit and ready between sales.
  useKeepAwake(isDark);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className={cn(
        'flex flex-col',
        // The kiosk view is locked to the viewport so a TV never scrolls.
        isDark ? 'h-[100dvh] overflow-hidden bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50'
      )}
    >
      <header
        className={cn(
          'relative z-20 flex shrink-0 items-center justify-between gap-3 px-4 py-3',
          isDark
            ? 'border-b border-white/10 bg-slate-950'
            : 'border-b border-white/30 bg-white/70 backdrop-blur-md'
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center shrink-0',
              isDark
                ? 'bg-gradient-to-br from-amber-300 to-amber-600 shadow-lg shadow-amber-900/40'
                : 'bg-amber-500 shadow-sm'
            )}
          >
            <Gift className={cn('h-5 w-5', isDark ? 'text-slate-900' : 'text-white')} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-base leading-tight truncate">Ödül Çekilişi</p>
            <p
              className={cn(
                'text-xs truncate',
                isDark ? 'text-slate-400' : 'text-muted-foreground'
              )}
            >
              Mağaza çekiliş uygulaması
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isDark && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={soundEnabled ? 'Sesi kapat' : 'Sesi aç'}
              aria-pressed={soundEnabled}
              className={cn(
                'rounded-xl',
                soundEnabled
                  ? 'text-amber-300 hover:bg-white/10 hover:text-amber-200'
                  : 'text-slate-500 hover:bg-white/10 hover:text-slate-300'
              )}
              onClick={() => setSoundEnabled(!soundEnabled)}
            >
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'rounded-xl',
              isDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-muted-foreground'
            )}
            onClick={() => void handleSignOut()}
          >
            <LogOut className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Çıkış</span>
          </Button>
        </div>
      </header>
      <main className={cn('relative flex-1', isDark && 'min-h-0')}>{children}</main>
    </div>
  );
}
